import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Header, 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbSeparator, 
  BreadcrumbPage, 
  Field, 
  CheckboxField,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  FileUpload,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  toast,
  Progress,
  Spinner
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import {
  Download,
  Upload,
  Plus,
  X,
  CheckCircle,
  XCircle,
  AlertCircle
} from '@/lib/icons';
import { batchUploadService, type BatchUploadStatus } from '@/services/batch-upload-service';
import { productService } from '@/services/product-service';

export function BatchUploadPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const [selectedProducts, setSelectedProducts] = useState<string[]>(['']); // Start with one empty selector
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [sendEmails, setSendEmails] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedError, setSelectedError] = useState<string>('');
  const previousStatusesRef = useRef<Record<string, string>>({});

  // Fetch available products
  const { data: productsData } = useQuery<{ data: Array<{ id: string; attributes?: { name?: string }; name?: string }> }>({
    queryKey: ['products-batch-upload'],
    queryFn: () => productService.listProducts({ active: true }) as Promise<{ data: Array<{ id: string; attributes?: { name?: string }; name?: string }> }>,
  });

  // Poll for job status (more frequently to catch quick jobs)
  const { data: jobStatuses, refetch: refetchStatuses } = useQuery({
    queryKey: ['batch-upload-status'],
    queryFn: () => batchUploadService.getJobStatus(),
    refetchInterval: 1000, // Poll every 1 second
  });

  // Filter to only upload jobs
  const uploadJobs = useMemo(
    () => jobStatuses?.data?.filter(job => job.job_type === 'upload') || [],
    [jobStatuses?.data]
  );

  // Monitor job status changes and show notifications
  useEffect(() => {
    if (!uploadJobs || uploadJobs.length === 0) return;

    uploadJobs.forEach((status) => {
      const previousStatus = previousStatusesRef.current[status.id];

      // Only show notification if status changed to completed or failed
      if (previousStatus && previousStatus !== status.status) {
        if (status.status === 'completed') {
          toast.success(`Batch Upload Complete: Successfully processed ${status.processed_count} of ${status.total_count} users.`);
          // Invalidate user caches so list and detail pages show fresh data
          queryClient.invalidateQueries({ queryKey: ['portal-users'] });
          queryClient.invalidateQueries({ queryKey: ['portal-user'] });
        } else if (status.status === 'completed_with_errors') {
          toast.error(`Batch Upload Completed with Errors: Processed ${status.processed_count} of ${status.total_count} users. Some users could not be created.`);
          // Invalidate user caches even on errors - some users may have been processed
          queryClient.invalidateQueries({ queryKey: ['portal-users'] });
          queryClient.invalidateQueries({ queryKey: ['portal-user'] });
        } else if (status.status === 'failed') {
          toast.error('Batch Upload Failed: The batch upload operation failed. Please try again.');
        }
      }
    });

    // Update previous statuses in ref (no re-render)
    uploadJobs.forEach((status) => {
      previousStatusesRef.current[status.id] = status.status;
    });
  }, [uploadJobs, queryClient]);
  
  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: () => batchUploadService.downloadTemplate(selectedProducts),
    onError: () => {
      toast.error('Failed to download template. Please try again.');
    },
  });

  // Download Excel template mutation
  const downloadExcelTemplateMutation = useMutation({
    mutationFn: () => batchUploadService.downloadExcelTemplate(selectedProducts),
    onError: () => {
      toast.error('Failed to download Excel template. Please try again.');
    },
  });
  
  // Upload process
  const processUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);
      setUploadProgress(0);
      
      try {
        // Get presigned URL
        setUploadProgress(10);
        const { upload_url, key } = await batchUploadService.getUploadUrl();
        
        // Upload to S3
        setUploadProgress(30);
        await batchUploadService.uploadToS3(file, upload_url);
        
        // Process upload
        setUploadProgress(70);
        await batchUploadService.processUpload(key, sendEmails);
        
        setUploadProgress(100);

        // Clear file and refetch statuses immediately and repeatedly for fast jobs
        setUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Refetch multiple times to catch quick completions
        refetchStatuses();
        setTimeout(() => refetchStatuses(), 500);
        setTimeout(() => refetchStatuses(), 1000);
        setTimeout(() => refetchStatuses(), 2000);
        
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error('Upload failed. Please try again.');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
  });
  
  const handleAddProduct = () => {
    setSelectedProducts([...selectedProducts, '']);
  };
  
  const handleProductChange = (index: number, value: string) => {
    const newProducts = [...selectedProducts];
    newProducts[index] = value;
    setSelectedProducts(newProducts);
  };
  
  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
  };
  
  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setUploadFile(file);
      } else {
        toast.error('Please select a CSV file');
        setUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } else {
      setUploadFile(null);
    }
  };
  
  const handleUpload = () => {
    if (uploadFile) {
      processUploadMutation.mutate(uploadFile);
    }
  };
  
  const showErrors = (errors: string) => {
    setSelectedError(errors);
    setShowErrorModal(true);
  };
  
  const getStatusIcon = (status: BatchUploadStatus) => {
    switch (status.status) {
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      case 'in_progress':
        return <Spinner size="small" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'completed_with_errors':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };
  
  const getStatusText = (status: BatchUploadStatus) => {
    switch (status.status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return `Processing... ${status.percentage_complete}%`;
      case 'completed':
        return 'Completed';
      case 'completed_with_errors':
        return 'Completed with errors';
      case 'failed':
        return 'Failed';
    }
  };
  
  return (
    <>
      <Header 
        title="Batch Upload Users"
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/users">Users</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Batch Upload</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />
      <ContentContainer className="space-y-2">
      
        {/* Download Template Section */}
        <Card size="medium">
          <CardHeader>
            <CardTitle>1. Download Template</CardTitle>
            <CardDescription>
              Select the tools/resources your users will have access to, then download the template.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 w-full">
          
            {selectedProducts.map((productId, index) => (
              <div key={index} className="flex items-center gap-2">
                <Select
                  value={productId}
                  onValueChange={(value) => handleProductChange(index, value)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select tool / resource..." />
                  </SelectTrigger>
                  <SelectContent>
                    {productsData?.data?.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.attributes?.name || product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => handleRemoveProduct(index)}
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive-dark"
                  aria-label={`Remove tool or resource ${index + 1}`}
                >
                  <X />
                </Button>
              </div>
            ))}
            
            <Button
              onClick={handleAddProduct}
              variant="ghost"
              className="w-fit"
            >
              <Plus />
              Add Another Tool or Resource
            </Button>
            
            <div className="flex gap-2">
              <Button
                onClick={() => downloadTemplateMutation.mutate()}
                disabled={downloadTemplateMutation.isPending}
              >
                {downloadTemplateMutation.isPending ? (
                  <Spinner size="small" color="light" />
                ) : (
                  <Download />
                )}
                Download CSV Template
              </Button>
              <Button
                onClick={() => downloadExcelTemplateMutation.mutate()}
                disabled={downloadExcelTemplateMutation.isPending}
                variant="lilac"
              >
                {downloadExcelTemplateMutation.isPending ? (
                  <Spinner size="small" color="light" />
                ) : (
                  <Download />
                )}
                Download Excel Template
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload Section */}
        <Card size="medium">
          <CardHeader>
            <CardTitle>2. Upload Users</CardTitle>
            <CardDescription>
              The batch upload now accepts most international date formats including MM/DD/YYYY, DD.MM.YYYY, and YYYY-MM-DD.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Field>
                <FileUpload
                  ref={fileInputRef}
                  label="Upload CSV file"
                  accept=".csv,text/csv"
                  onFilesChange={handleFileSelect}
                  buttonText="Choose File"
                  showFileName={true}
                />
              </Field>
              
              {isUploading && (
                <Progress 
                  value={uploadProgress} 
                  max={100}
                  size="sm"
                />
              )}

              <CheckboxField
                checked={sendEmails}
                onCheckedChange={(checked: boolean) => setSendEmails(!!checked)}
                label="Send invite email to all email addresses in file"
              />
              
              <Button
                onClick={handleUpload}
                disabled={!uploadFile || isUploading}
              >
                {isUploading ? (
                  <Spinner size="small" color="light" />
                ) : (
                  <Upload />
                )}
                Upload CSV
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Recent Jobs Status */}
        {uploadJobs.length > 0 && (
          <Card size="medium">
            <CardHeader>
              <CardTitle>Recent Upload Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {uploadJobs.map((status) => (
                  <div key={status.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status)}
                      <div>
                        <p className="font-medium">{getStatusText(status)}</p>
                        <p className="text-sm text-muted-foreground">
                          Started {new Date(status.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {status.total_count && (
                        <span className="text-sm text-muted-foreground">
                          {status.processed_count} / {status.total_count} users
                        </span>
                      )}
                      {status.has_errors && (
                        <Button
                          onClick={() => showErrors(status.errors || '')}
                          variant="outline"
                        >
                          View Errors
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Error Modal */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Upload Errors</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh]">
              <pre className="text-sm whitespace-pre-wrap">{selectedError}</pre>
            </div>
            <DialogFooter>
              <Button
                onClick={() => setShowErrorModal(false)}
                variant="outline"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </>
  );
}